import { useState, useEffect } from "react";
import { Package, ArrowRight, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { shopApi } from "@/shop/shopApi";
import { Reveal } from "@/shop/motion";

export function TrackOrder() {
  const [store, setStore] = useState("");
  useEffect(() => { shopApi.config().then((d) => setStore(d.store_domain)).catch(() => {}); }, []);
  const goStatus = () => {
    if (store) window.open(`https://${store}/account`, "_blank");
    else toast.info("Order tracking opens once the store is connected.");
  };  return (
    <div className="pt-28 pb-28 min-h-[70vh]" data-testid="track-order-page">
      <div className="max-w-xl mx-auto px-5 md:px-10 text-center">
        <Reveal>
          <div className="w-14 h-14 mx-auto rounded-full bg-sh-blue/15 grid place-items-center mb-6"><Package className="w-6 h-6 text-sh-blue" strokeWidth={1.5} /></div>
          <p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">Track Order</p>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-[-0.03em]">{`Where's my order?`}</h1>
          <p className="text-white/60 mt-5 leading-relaxed">Every order ships with an email tracking link. You can also view live status and history in your Shopify account.</p>
          <button onClick={goStatus} className="mt-10 inline-flex items-center gap-2.5 h-14 px-9 rounded-full bg-white text-black font-medium hover:bg-sh-blue hover:text-white transition-colors" data-testid="track-order-cta">
            View order status <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-white/40 mt-6">Check your inbox for your order confirmation and tracking number.</p>
        </Reveal>
      </div>
    </div>
  );
}

export function ShopAbout() {
  return (
    <div className="pt-28 pb-28" data-testid="shop-about-page">
      <div className="max-w-3xl mx-auto px-5 md:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">About</p>
          <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.03em] leading-[0.95]">Cut through<br/>the noise.</h1>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 space-y-6 text-lg text-white/65 leading-relaxed">
            <p>Signal House exists for people who care about the details. We build and curate products that earn their place — no filler, no gimmicks, just gear engineered to perform and last.</p>
            <p>{`Behind the scenes, our commerce runs on Shopify for rock-solid checkout, inventory, and fulfillment. Up front, it's a completely custom experience built for speed and craft.`}</p>
            <p>Founded by James Douglas as an extension of the James 1:1 creative practice — the same obsession with quality, applied to the things you use every day.</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-12">
            {[["10k+", "Orders shipped"], ["4.9★", "Avg. rating"], ["48h", "Ship time"], ["30d", "Easy returns"]].map(([n, l]) => (
              <div key={l}><p className="font-display text-4xl font-bold text-sh-blue">{n}</p><p className="text-sm text-white/50 mt-1">{l}</p></div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export function ShopContact() {
  const [form, setForm] = useState({ first_name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) { toast.error("All fields are required."); return; }
    setBusy(true);
    try {
      await api.post("/contact", { first_name: form.first_name, last_name: "", email: form.email, subject: "Signal House inquiry", message: form.message });
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ first_name: "", email: "", message: "" });
    } catch { toast.error("Could not send. Try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="pt-28 pb-28" data-testid="shop-contact-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">Contact</p>
          <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.03em]">{`Let's talk.`}</h1>
          <p className="text-white/60 mt-6 leading-relaxed max-w-md">Questions about a product, an order, or a wholesale inquiry? Drop us a line and a human will get back within one business day.</p>
          <ul className="mt-10 space-y-4 text-sm">
            <li className="flex items-center gap-3"><Mail className="w-5 h-5 text-sh-blue" strokeWidth={1.4} /> <a href="mailto:james@jamesone.co" className="text-white/80 hover:text-white">james@jamesone.co</a></li>
            <li className="flex items-center gap-3"><MapPin className="w-5 h-5 text-sh-blue" strokeWidth={1.4} /> Florida · Worldwide shipping</li>
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-5 border border-white/10 rounded-2xl p-8 bg-[#0e0e10]" data-testid="shop-contact-form">
            <Field label="Name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="contact-name" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Message</label>
              <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/15 focus:border-sh-blue outline-none rounded-xl p-4 text-white resize-none" data-testid="contact-message" />
            </div>
            <button type="submit" disabled={busy} className="w-full h-14 rounded-full bg-sh-blue text-white font-medium hover:bg-[#3b9bff] transition-colors disabled:opacity-50" data-testid="contact-submit">{busy ? "Sending…" : "Send message"}</button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-white/50">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full h-12 bg-white/5 border border-white/15 focus:border-sh-blue outline-none rounded-xl px-4 text-white" data-testid={testid} />
    </div>
  );
}
